import { useEffect, useMemo, useRef, useState } from 'react'

import { makeVar, useReactiveVar } from '@apollo/client'
import { useInView } from 'react-intersection-observer'

import { useGlobalStaticStateContext } from './useGlobalStaticState'

export const REACTION_LAYER_KEY = 'reaction-layer-click'
export const tooltipExposedReviewGroupIdVar = makeVar<string | null>(null)

interface Props {
  reviewGroupId: string
  skip: boolean
}

export const useReactionTooltip = ({ reviewGroupId, skip: _skip }: Props) => {
  const { isSsr } = useGlobalStaticStateContext()
  const tooltipExposedReviewGroupId = useReactiveVar(tooltipExposedReviewGroupIdVar)
  const [showReactionTooltip, setShowReactionTooltip] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  const skip = useMemo(() => {
    if (isSsr) return true

    if (localStorage.getItem(REACTION_LAYER_KEY)) return true

    if (_skip) return true

    if (tooltipExposedReviewGroupId && tooltipExposedReviewGroupId !== reviewGroupId) return true

    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSsr, tooltipExposedReviewGroupId, reviewGroupId, _skip])

  const { ref } = useInView({
    threshold: 0.8,
    rootMargin: '-10% 0px',
    skip,
    onChange: inView => {
      if (!inView) return

      if (tooltipExposedReviewGroupIdVar() === null) {
        tooltipExposedReviewGroupIdVar(reviewGroupId)
      }

      if (tooltipExposedReviewGroupIdVar() === reviewGroupId && !timerRef.current) {
        setShowReactionTooltip(true)
        timerRef.current = setTimeout(() => {
          setShowReactionTooltip(false)
        }, 5000)
      }
    },
  })

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const closeTooltip = () => {
    if (tooltipExposedReviewGroupId === reviewGroupId) {
      setShowReactionTooltip(false)
      localStorage.setItem(REACTION_LAYER_KEY, 'true')
    }
  }

  useEffect(() => {
    return () => {
      tooltipExposedReviewGroupIdVar(null)
    }
  }, [])

  return { ref, showReactionTooltip, closeTooltip }
}

export default useReactionTooltip
